import { useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { SlidersHorizontal, X } from "lucide-react";

export default function Shop() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState("newest");
  const [priceRange, setPriceRange] = useState([0, 5000]);
  const [showFilters, setShowFilters] = useState(false);

  const category = searchParams.get("category");
  const search = searchParams.get("search");

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data));
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (category) params.category = category;
    if (search) params.search = search;
    if (sort !== "newest") params.sort = sort;
    if (priceRange[0] > 0) params.min_price = priceRange[0];
    if (priceRange[1] < 5000) params.max_price = priceRange[1];
    api.get("/products", { params }).then((r) => setProducts(r.data)).finally(() => setLoading(false));
  }, [category, search, sort, priceRange]);

  const heading = useMemo(() => {
    if (search) return `Results for "${search}"`;
    if (category) {
      const c = categories.find((cc) => cc.id === category);
      return c ? c.name : "Shop";
    }
    return "All Products";
  }, [category, search, categories]);

  const setCategory = (id) => {
    const newParams = new URLSearchParams(searchParams);
    if (id) newParams.set("category", id);
    else newParams.delete("category");
    newParams.delete("search");
    setSearchParams(newParams);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-12" data-testid="shop-page">
      {/* Header */}
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-rani font-bold">Shop</p>
          <h1 className="font-display font-bold text-deepbrown text-3xl sm:text-4xl lg:text-5xl mt-2" data-testid="shop-heading">
            {heading}
          </h1>
        </div>
        <div className="flex gap-3 items-center">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="lg:hidden inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-deepbrown text-ivory text-sm font-bold"
            data-testid="filters-toggle"
          >
            <SlidersHorizontal className="h-4 w-4" /> Filters
          </button>
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger className="w-44 rounded-full bg-white border-deepbrown/20" data-testid="sort-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="newest">Newest</SelectItem>
              <SelectItem value="price_asc">Price: Low to High</SelectItem>
              <SelectItem value="price_desc">Price: High to Low</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Filters */}
        <aside className={`lg:col-span-3 ${showFilters ? "block" : "hidden lg:block"}`}>
          <div className="bg-white rounded-2xl p-6 border border-deepbrown/10 sticky top-24">
            <div className="flex items-center justify-between lg:hidden mb-4">
              <h3 className="font-display text-xl">Filters</h3>
              <button onClick={() => setShowFilters(false)}><X className="h-5 w-5" /></button>
            </div>

            <h3 className="font-display text-lg text-deepbrown mb-3">Categories</h3>
            <div className="flex flex-col gap-1">
              <button
                onClick={() => setCategory(null)}
                className={`text-left px-3 py-2 rounded-lg text-sm transition ${!category ? "bg-rani text-white font-bold" : "text-deepbrown/70 hover:bg-marigold/10"}`}
                data-testid="filter-all"
              >
                All
              </button>
              {categories.map((c) => (
                <button
                  key={c.id}
                  onClick={() => setCategory(c.id)}
                  className={`text-left px-3 py-2 rounded-lg text-sm transition ${category === c.id ? "bg-rani text-white font-bold" : "text-deepbrown/70 hover:bg-marigold/10"}`}
                  data-testid={`filter-${c.id}`}
                >
                  {c.name}
                </button>
              ))}
            </div>

            <div className="border-t border-deepbrown/10 mt-6 pt-6">
              <h3 className="font-display text-lg text-deepbrown mb-2">Price</h3>
              <p className="text-sm text-deepbrown/70 mb-3">
                ₹{priceRange[0].toLocaleString("en-IN")} – ₹{priceRange[1].toLocaleString("en-IN")}
              </p>
              <Slider
                value={priceRange}
                onValueChange={setPriceRange}
                min={0}
                max={5000}
                step={100}
                data-testid="price-slider"
              />
            </div>
          </div>
        </aside>

        {/* Products */}
        <main className="lg:col-span-9">
          {loading ? (
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-6">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="aspect-[3/4] rounded-2xl bg-deepbrown/5 animate-pulse" />
              ))}
            </div>
          ) : products.length === 0 ? (
            <div className="bg-white rounded-2xl p-12 text-center border border-deepbrown/10">
              <p className="font-display text-2xl text-deepbrown">No products found</p>
              <p className="text-deepbrown/60 mt-2">Try adjusting your filters or search.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-6">
              {products.map((p) => <ProductCard key={p.id} product={p} />)}
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
